import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { Divider, Flex, Grid, Link, Text } from '@servicetitan/anvil2';

import { formatCurrency } from '../../../utils/format-currency';
import { scrollToBottom } from '../../../utils/scroll-to-bottom';
import { EstimateItemModel } from '../../../models/estimate.model';
import { InvoiceTask } from '../../../models/invoice.model';
import { MessageCard } from '../../message-card/message-card';

interface EstimateTaskProps {
    data: InvoiceTask;
}

const EstimateTask: FC<EstimateTaskProps> = ({ data }) => {
    return (
        <div>
            <div className="m-b-1">
                <Text variant="eyebrow" className="m-b-half" size="small">
                    Name
                </Text>
                <Text size="small">{data.name}</Text>
            </div>

            <Grid
                templateColumns="1fr 1fr 1fr"
                gap="2"
                justifyContent="space-between"
                alignItems="stretch"
                className="m-b-1"
            >
                <Flex direction="column">
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Code
                    </Text>
                    <Text size="small">{data.code}</Text>
                </Flex>
                <Flex direction="column">
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Quantity
                    </Text>
                    <Text size="small">{data.quantity}</Text>
                </Flex>

                <Flex direction="column">
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Total
                    </Text>
                    <Text size="small">{formatCurrency(data.total)}</Text>
                </Flex>
            </Grid>
        </div>
    );
};

interface EstimateTasksProps {
    data: EstimateItemModel;
}

export const EstimateTasks: FC<EstimateTasksProps> = ({ data }) => {
    const [showMore, setShowMore] = useState(false);

    const estimateTasks = useMemo(() => {
        if (showMore) {
            return data.estimate_tasks ?? [];
        }
        return (data.estimate_tasks ?? []).slice(0, 3);
    }, [showMore, data.estimate_tasks]);

    const toggleShowAll = useCallback(() => {
        setShowMore(!showMore);

        if (!showMore) {
            scrollToBottom();
        }
    }, [setShowMore, showMore]);

    if ((estimateTasks ?? []).length === 0) {
        return null;
    }

    return (
        <MessageCard className="m-b-2" flexDirection="column">
            <Text variant="headline" el="h3" size="small">
                Tasks
            </Text>

            {estimateTasks.map((task, index) => {
                return (
                    <Fragment key={task.task_id}>
                        <EstimateTask data={task} />
                        {index !== estimateTasks.length - 1 && <Divider spacing={2} />}
                    </Fragment>
                );
            })}

            {(data.estimate_tasks ?? []).length > 3 && (
                <div>
                    <Link quiet appearance="primary" className="m-y-1" onClick={toggleShowAll}>
                        {showMore ? 'View fewer estimate tasks' : 'View all estimate tasks'}
                    </Link>
                </div>
            )}

            <Flex gap="2" justifyContent="space-between" alignItems="stretch">
                <Text size="small">
                    <b>Estimate Total</b>
                </Text>

                <Text size="small">
                    <b>{formatCurrency(data.estimate_total)}</b>
                </Text>
            </Flex>
        </MessageCard>
    );
};
